const { elastic } = require('../clients')
const {
    generateEndPoint,
    generateFilter,
    generateQuery,
    formatResult
} = require('../helpers')

class Repository {
    constructor({ source, path } = {}) {
        this.source = source || 'elastic'
        this.path =
            path || this.constructor.name.split('Repository')[0].toLowerCase()
        this.action = generateEndPoint({ source: this.source, path: this.path })

        this.reset()
    }

    reset() {
        this.highlight = false
        this.filters = []
        this.params = {
            fields: [],
            limit: 12,
            page: 1,
            sort: []
        }

        this.index = 0
    }

    where(...params) {
        if (params.length) {
            if (!this.filters[this.index]) {
                this.filters[this.index] = []
            }

            if (typeof params[0] === 'function') {
                const [callback] = params

                const instance = new this.constructor()
                callback(instance)

                const { filters } = instance
                this.filters[this.index].push(filters)
            } else if (params[1] && Array.isArray(params[1])) {
                this.where((q) => {
                    for (const index in params[1]) {
                        if (index < 1) {
                            q.where(params[0], params[1][index])
                        } else {
                            q.orWhere(params[0], params[1][index])
                        }
                    }
                })
            } else {
                this.filters[this.index].push(generateFilter(params))
            }

            this.index = this.filters.length
        }

        return this
    }

    orWhere(...params) {
        this.index = this.filters.length - 1
        return this.where(...params)
    }

    whereNot(...params) {
        return this.where(params[0], '!=', params[1])
    }

    whereExists(...params) {
        return this.where(params[0], '!+', params[1])
    }

    whereNotExists(...params) {
        return this.where(params[0], '!-', params[1])
    }

    whereGt(...params) {
        return this.where(params[0], '>', params[1])
    }

    whereGte(...params) {
        return this.where(params[0], '>=', params[1])
    }

    whereLt(...params) {
        return this.where(params[0], '<', params[1])
    }

    whereLte(...params) {
        return this.where(params[0], '<=', params[1])
    }

    limit(value) {
        this.params.limit = value
        return this
    }

    page(value) {
        this.params.page = value
        return this
    }

    sort(value) {
        if (Array.isArray(value)) {
            this.params.sort = value
        } else {
            this.params.sort.push(value)
        }

        return this
    }

    fields(value) {
        this.params.fields = value
        return this
    }

    query() {
        const { fields, limit, page, sort, customQuery } = this.params
        const start = limit * (page - 1)

        let query = {}
        if (customQuery) {
            query = customQuery
        } else {
            query = {
                query: generateQuery({
                    source: this.source,
                    filters: this.filters
                })
            }
        }

        if (this.source === 'elastic') {
            query._source = fields
            query.from = start
            query.size = limit
            query.sort = sort

            if (this.highlight) {
                query.highlight = this.highlight
            }
        }

        query.sort.push({
            _score: {
                order: 'desc'
            }
        })
        return query
    }

    customQuery(query) {
        this.params.customQuery = { query }
        return this
    }

    async all() {
        return this.limit(9999)
            .page(1)
            .get()
            .then((res) => res.data)
    }

    async get(reset) {
        const query = this.query()

        if (this.source === 'elastic') {
            try {
                const result = await elastic.post(this.action, query)
                const resultFormatted = formatResult(
                    this.source,
                    this.params,
                    result
                )

                if (reset) {
                    this.reset()
                }

                return resultFormatted
            } catch (e) {
                throw new Error(e)
            }
        } else {
            // todo strapi get
            // console.log(JSON.stringify(query));
        }
    }

    async getById(id) {
        this.filters = []
        this.where('id', id)

        return await this.first()
    }

    async first() {
        const { data } = await this.get()

        if (!data || !data.length) {
            return null
        }

        return data[0]
    }
}

module.exports = Repository
